import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Truck, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

export const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    paymentMethod: 'cod'
  });
  const [errors, setErrors] = useState({});

  const shippingCost = cartTotal >= 999 ? 0 : 99;
  const totalWithShipping = cartTotal + shippingCost;

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) newErrors.zipCode = 'Invalid ZIP code';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    const orderNumber = `BV-${Date.now().toString().slice(-8)}`;
    
    setTimeout(() => {
      clearCart();
      navigate('/order-confirmation', { 
        state: { 
          orderNumber,
          orderDetails: {
            ...formData,
            items: cartItems,
            subtotal: cartTotal,
            shipping: shippingCost,
            total: totalWithShipping
          }
        }
      });
    }, 1500);
  };

  if (cartItems.length === 0) {
    return (
      <main className="pt-32 pb-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#2D4A3E] mb-4">Your cart is empty</h1>
          <Link to="/category/all" className="text-[#C67D4E] font-['Montserrat']">
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24 bg-[#FAF8F5]" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm font-['Montserrat'] mb-8" data-testid="checkout-breadcrumb">
          <Link to="/cart" className="text-[#8B7355] hover:text-[#2D4A3E]">Cart</Link>
          <ChevronRight size={14} className="text-[#E8E4DE]" />
          <span className="text-[#2D4A3E]">Checkout</span>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mb-8 md:mb-12" data-testid="checkout-title">
            Checkout
          </h1>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <div className="bg-white border border-[#E8E4DE] p-6 md:p-8">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <Label htmlFor="firstName" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.firstName ? 'border-red-500' : ''}`}
                      data-testid="input-firstname"
                    />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.lastName ? 'border-red-500' : ''}`}
                      data-testid="input-lastname"
                    />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.lastName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="input-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white border border-[#E8E4DE] p-6 md:p-8">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Shipping Address</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <Label htmlFor="address" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.address ? 'border-red-500' : ''}`}
                      data-testid="input-address"
                    />
                    {errors.address && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.address}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <Label htmlFor="city" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.city ? 'border-red-500' : ''}`}
                        data-testid="input-city"
                      />
                      {errors.city && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.city}</p>}
                    </div>
                    <div>
                      <Label htmlFor="state" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">State *</Label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={`mt-2 w-full h-10 px-3 rounded-sm border border-[#E8E4DE] focus:border-[#2D4A3E] focus:outline-none font-['Montserrat'] text-sm bg-white ${errors.state ? 'border-red-500' : ''}`}
                        data-testid="input-state"
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      {errors.state && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.state}</p>}
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className={`mt-2 rounded-sm border-[#E8E4DE] focus:border-[#2D4A3E] font-['Montserrat'] ${errors.zipCode ? 'border-red-500' : ''}`}
                        data-testid="input-zipcode"
                      />
                      {errors.zipCode && <p className="text-red-500 text-xs mt-1 font-['Montserrat']">{errors.zipCode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white border border-[#E8E4DE] p-6 md:p-8">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Payment Method</h2>
                <RadioGroup
                  value={formData.paymentMethod}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, paymentMethod: value }))}
                  className="space-y-4"
                >
                  <div className={`flex items-center space-x-3 p-4 border rounded-sm ${formData.paymentMethod === 'cod' ? 'border-[#C67D4E] bg-[#FAF8F5]' : 'border-[#E8E4DE]'}`}>
                    <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" />
                    <Label htmlFor="cod" className="flex-1 cursor-pointer">
                      <span className="font-['Montserrat'] font-semibold text-[#2D4A3E]">Cash on Delivery (COD)</span>
                      <p className="font-['Montserrat'] text-xs text-[#8B7355] mt-1">Pay when your order arrives</p>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 border rounded-sm ${formData.paymentMethod === 'card' ? 'border-[#C67D4E] bg-[#FAF8F5]' : 'border-[#E8E4DE]'} opacity-60`}>
                    <RadioGroupItem value="card" id="card" disabled data-testid="payment-card" />
                    <Label htmlFor="card" className="flex-1 cursor-not-allowed">
                      <span className="font-['Montserrat'] font-semibold text-[#2D4A3E]">Credit/Debit Card</span>
                      <p className="font-['Montserrat'] text-xs text-[#8B7355] mt-1">Coming soon</p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-[#E8E4DE] p-6 md:p-8 sticky top-32" data-testid="checkout-summary">
                <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Order Summary</h2>

                {/* Cart Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-3">
                      <div className="w-14 h-14 bg-[#FAF8F5] flex-shrink-0 border border-[#E8E4DE]">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-['Montserrat'] text-xs text-[#2D4A3E] line-clamp-1">{item.name}</h4>
                        <p className="font-['Montserrat'] text-[10px] text-[#8B7355]">Qty: {item.quantity}</p>
                        <p className="font-['Montserrat'] text-sm font-semibold text-[#2D4A3E]">${(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#E8E4DE] pt-4 space-y-3 font-['Montserrat']">
                  <div className="flex justify-between text-sm text-[#5A6B5D]">
                    <span>Subtotal</span>
                    <span>${cartTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[#5A6B5D]">
                    <span>Shipping</span>
                    <span className={shippingCost === 0 ? 'text-[#10B981] font-semibold' : ''}>
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost}`}
                    </span>
                  </div>
                  <div className="border-t border-[#E8E4DE] pt-3">
                    <div className="flex justify-between text-lg font-semibold text-[#2D4A3E]">
                      <span>Total</span>
                      <span data-testid="checkout-total">${totalWithShipping.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-8 bg-[#C67D4E] text-white hover:bg-[#A86840] py-6 rounded-sm text-xs uppercase tracking-wider font-semibold disabled:opacity-50 font-['Montserrat']"
                  data-testid="place-order-btn"
                >
                  {isSubmitting ? 'Processing...' : 'Place Order'}
                </Button>

                {/* Trust Signals */}
                <div className="mt-6 pt-6 border-t border-[#E8E4DE] space-y-3">
                  <div className="flex items-center gap-3 text-sm text-[#5A6B5D]">
                    <Shield size={16} className="text-[#C67D4E]" />
                    <span className="font-['Montserrat'] text-xs">Secure Checkout</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#5A6B5D]">
                    <Truck size={16} className="text-[#C67D4E]" />
                    <span className="font-['Montserrat'] text-xs">
                      {shippingCost === 0 ? 'Free Shipping Included' : 'Fast Delivery Across USA'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
};
